import { useEffect, useMemo, useRef } from 'react'

import { useSelector } from 'react-redux'

import type { RecipeUUID } from '@library/menu-service'

import { useIsMenuExperiencePageEnabled } from 'routes/Menu/domains/menu'
import { getBasketMenuUuid, getBasketRecipes, getNumPortions } from 'selectors/basket'
import { getBasketRecipeNumPortions, getBasketRecipeQuantity } from 'utils/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { useMenuExperienceRecipeTileRefresh } from './useMenuExperienceRecipeTileRefresh'

type MenuExperienceBasketRecipeTileSyncQuery = MenuQueryParams &
  Record<string, unknown> & { collection?: string }

export const useMenuExperienceBasketRecipeTileSync = (
  query: MenuExperienceBasketRecipeTileSyncQuery,
) => {
  const isMenuExperiencePageEnabled = useIsMenuExperiencePageEnabled()
  const menuId = useSelector(getBasketMenuUuid)
  const basketRecipes = useSelector(getBasketRecipes)
  const basketNumPortions = useSelector(getNumPortions)
  const { refreshRecipeTile } = useMenuExperienceRecipeTileRefresh({ menuId, query })
  const previousSnapshotRef = useRef(new Map<RecipeUUID, number>())
  const previousMenuIdRef = useRef(menuId)
  const wasEnabledRef = useRef(false)

  const snapshot = useMemo(() => {
    const nextSnapshot = new Map<RecipeUUID, number>()

    basketRecipes.forEach((recipe, recipeId) => {
      const quantity = getBasketRecipeQuantity(recipe)

      if (quantity > 0) {
        nextSnapshot.set(recipeId, getBasketRecipeNumPortions(recipe, basketNumPortions))
      }
    })

    return nextSnapshot
  }, [basketNumPortions, basketRecipes])

  useEffect(() => {
    const hasMenuChanged = previousMenuIdRef.current !== menuId
    const shouldReconcileAll = !wasEnabledRef.current || hasMenuChanged
    const previousSnapshot = shouldReconcileAll
      ? new Map<RecipeUUID, number>()
      : previousSnapshotRef.current

    previousSnapshotRef.current = snapshot
    previousMenuIdRef.current = menuId
    wasEnabledRef.current = isMenuExperiencePageEnabled

    if (!isMenuExperiencePageEnabled) {
      return
    }

    snapshot.forEach((numPortions, recipeId) => {
      const previousNumPortions = previousSnapshot.get(recipeId)
      const hasCustomPortionsBeenAdded =
        previousNumPortions === undefined && numPortions !== basketNumPortions
      const havePortionsChanged =
        previousNumPortions !== undefined && previousNumPortions !== numPortions

      if (hasCustomPortionsBeenAdded || havePortionsChanged) {
        void refreshRecipeTile(recipeId, numPortions)
      }
    })

    previousSnapshot.forEach((_numPortions, recipeId) => {
      if (!snapshot.has(recipeId)) {
        void refreshRecipeTile(recipeId, basketNumPortions)
      }
    })
  }, [basketNumPortions, isMenuExperiencePageEnabled, menuId, refreshRecipeTile, snapshot])
}
